import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Wrench, Briefcase, Flag, Activity, TrendingUp, CheckCircle, Clock, FileText } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_users: 0,
    total_tradies: 0,
    total_services: 0,
    pending_verifications: 0,
    active_jobs: 0,
    total_quotes: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchRecentActivity();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      // Fetch REAL stats from backend
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await axios.get(`${API}/admin/activity-logs?limit=5`);
      setRecentActivity(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch recent activity');
    }
  };

  const statCards = [
    // GoTradies
    {
      title: 'GoTradies - Active Services',
      value: stats.total_services,
      icon: Wrench,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900',
      link: '/admin/services'
    },
    {
      title: 'GoTradies - Tradies',
      value: stats.total_tradies,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900',
      link: '/admin/tradies'
    },
    {
      title: 'GoTradies - Pending Verifications',
      value: stats.pending_verifications,
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900',
      link: '/admin/verification'
    },
    // GoJobs
    {
      title: 'GoJobs - Active Listings',
      value: stats.active_jobs,
      icon: Briefcase,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900',
      link: '/admin/gojobs'
    },
    {
      title: 'GoJobs - Applications (7d)',
      value: stats.applications_7d || 0,
      icon: FileText,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900',
      link: '/admin/applications'
    },
    // GoTalent
    {
      title: 'GoTalent - Active Candidates',
      value: stats.talent_active || 0,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-100 dark:bg-green-900',
      link: '/admin/talent'
    },
    {
      title: 'GoTalent - Employer Passes',
      value: stats.talent_passes || 0,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100 dark:bg-green-900',
      link: '/admin/talent'
    },
    // General
    {
      title: 'Total Users',
      value: stats.total_users,
      icon: Users,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-100 dark:bg-cyan-900',
      link: '/admin/users'
    },
    {
      title: 'Total Companies',
      value: stats.total_companies || 0,
      icon: Activity,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-100 dark:bg-cyan-900',
      link: '/admin/companies'
    },
  ];

  const quickActions = [
    { label: 'View Activity Logs', path: '/admin/logs', icon: FileText },
    { label: 'Manage Feature Flags', path: '/admin/feature-flags', icon: Flag },
    { label: 'Verify Tradies', path: '/admin/tradies', icon: CheckCircle },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Platform Console</h1>
          <p className="text-muted-foreground">GoFella internal operations dashboard</p>
        </div>

        {/* Stats Grid - Organized by Product */}
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-4">Key Metrics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statCards.map((stat, index) => (
            <Card 
              key={index}
              className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-border"
              onClick={() => navigate(stat.link)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-foreground">{loading ? '...' : stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-xl font-bold text-foreground mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={() => navigate(action.path)}
                className="flex items-center gap-3 px-6 py-4 bg-card border border-border rounded-xl hover:bg-muted transition-colors text-left"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <action.icon className="w-5 h-5 text-primary" />
                </div>
                <span className="font-medium text-foreground">{action.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  No recent activity
                </div>
              ) : (
                recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                    <div className={`w-2 h-2 rounded-full ${
                      activity.result === 'success' ? 'bg-green-500' : 
                      activity.result === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                    }`}></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{activity.action_type.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-muted-foreground">{activity.user_email || 'System'}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(activity.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <button
              onClick={() => navigate('/admin/logs')}
              className="mt-4 text-sm text-primary hover:underline font-medium"
            >
              View all activity →
            </button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
